import React, { useContext, useEffect, useState } from "react";
import { Container, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useMutation } from "@apollo/client";
import SocketContext from "../../core/socketNotifications/SocketContext";
import history from "../../history";
import useQueryResponse from "../../lib/hooks/useQueryResponse";
import getPendingWorkerRequests from "./getPendingWorkerRequests.graphql";
import {
  RESPOND_TIME,
  RESPOND_MATERIALS,
  RESPOND_WORKERS,
  CONTEST,
  respondToWorkerRequest,
  contestWorkerRequest,
} from "../../components/WorkerRequestModal/workerRequestActions";

const JobNotifications = () => {
  const socket = useContext(SocketContext);
  const userId = useSelector((state) => state?.userAccount?.data?.userId);

  const { data, loading, refetch } = useQueryResponse({
    query: getPendingWorkerRequests,
    variables: {},
    ssr: false,
    fetchPolicy: "network-only",
  });

  const [respondTime] = useMutation(RESPOND_TIME);
  const [respondMaterials] = useMutation(RESPOND_MATERIALS);
  const [respondWorkers] = useMutation(RESPOND_WORKERS);
  const [contestReq] = useMutation(CONTEST);
  const [busyId, setBusyId] = useState(0);

  useEffect(() => {
    if (socket && userId) {
      const onEvent = () => { try { refetch(); } catch (e) { /* ignore */ } };
      socket.on(`workerRequest-${userId}`, onEvent);
      socket.on(`finalBillDue-${userId}`, onEvent);
      return () => {
        socket.off(`workerRequest-${userId}`, onEvent);
        socket.off(`finalBillDue-${userId}`, onEvent);
      };
    }
  }, [socket, userId, refetch]);

  const results = (data?.getPendingWorkerRequests?.results || []).filter(Boolean);

  const mutators = { respondTime, respondMaterials, respondWorkers, contestReq };

  const respond = async (r, accept) => {
    setBusyId(r.id);
    try {
      await respondToWorkerRequest({ r, accept, mutators });
      await refetch();
    } catch (e) { /* ignore */ }
    setBusyId(0);
  };

  const contest = async (r) => {
    setBusyId(r.id);
    try {
      const done = await contestWorkerRequest({ r, mutators });
      if (done) await refetch();
    } catch (e) { /* ignore */ }
    setBusyId(0);
  };

  return (
    <div className="subPageContainer">
      <Container>
        <div className="subPageWrapper">
          <h3 style={{ marginBottom: 20 }}>Job Notifications</h3>
          {loading && !results.length && <p>Loading…</p>}
          {!loading && !results.length && (
            <p style={{ color: "#888" }}>No requests right now. Your worker&apos;s requests will appear here.</p>
          )}
          {results.map((r) => (
            <div key={`${r.type}-${r.id}`} style={{ background: "#fff", borderRadius: 12, padding: 18, marginBottom: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong>{r.title}</strong>
                <strong style={{ color: "#d6002a" }}>{r.currency || ""} {Number(r.amount || 0).toFixed(2)}</strong>
              </div>
              <div style={{ color: "#777", marginTop: 6 }}>{r.detail}</div>
              {r.type === "finalBill" ? (
                <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <Button variant="danger" onClick={() => history.push(`/tracking/${r.bookingId}`)}>Settle now</Button>
                  <span style={{ color: "#777", fontStyle: "italic" }}>
                    Settle the remaining balance, or update your card — we&apos;ll re-attempt the charge automatically.
                  </span>
                </div>
              ) : (
                <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center" }}>
                  <Button variant="outline-danger" disabled={busyId === r.id} onClick={() => respond(r, false)}>Decline</Button>
                  <Button variant="danger" disabled={busyId === r.id} onClick={() => respond(r, true)}>Accept</Button>
                  <Button variant="link" disabled={busyId === r.id} onClick={() => contest(r)} style={{ color: "#777" }}>Contest / propose a fair price</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default JobNotifications;
